import { useEffect, useRef, useState } from 'react'
import Layout from '../system/Layout'
import { useAppDispatch, useAppSelector } from '../../shared/redux/hooks'
import { deleteNote, getNotes, updateNote } from './Notes.slice'
import NoteItem from './components/NoteItem'
import Panel from '../../components/Panel'
import Input from '../../components/Form/Input'
import Datepicker from 'react-tailwindcss-datepicker'
import classnames from 'classnames'
import { getInputStyles } from '../../components/Form/styles'
import Paginator from '../../components/Table/Paginator'
import { useAuth0 } from '@auth0/auth0-react'
import { Note, NoteSearchParams } from '../../apis/types'
import ComboboxMultiselect from './components/ComboboxMultiselect'
import Modal from '../../components/Modal'

const Notes: React.FC = () => {
  const dispatch = useAppDispatch()
  const { notes, total, resultsPerPage, page } = useAppSelector((state) => state.notes)
  const { user } = useAuth0()
  const [search, setSearch] = useState('')
  const [datePickerValue, setDatePickerValue] = useState<{
    startDate: Date | null
    endDate: Date | null
  }>({
    startDate: null,
    endDate: null,
  })
  const [confirmModal, setConfirmModal] = useState<React.ReactElement | boolean>(false)
  const [filterParams, setFilterParams] = useState<NoteSearchParams>({ sortIsPinned: 'desc', sortCreatedAt: 'desc' })
  const [listLoading, setListLoading] = useState(true)
  const listLoadGeneration = useRef(0)

  useEffect(() => {
    const generation = ++listLoadGeneration.current
    setListLoading(true)
    void dispatch(getNotes(filterParams)).finally(() => {
      if (generation === listLoadGeneration.current) {
        setListLoading(false)
      }
    })
  }, [dispatch, filterParams])

  const handlePaginatorClick = (page: number): void => {
    setFilterParams({
      ...filterParams,
      page,
    })
  }

  const handleNoteUpdate = async (noteCuid: string, body: string, tags: string[]) => {
    await dispatch(updateNote({ noteCuid, body, tags }))
    dispatch(getNotes(filterParams))
  }

  const handleNoteTogglePin = async (note: Note) => {
    await dispatch(updateNote({ noteCuid: note.cuid, isPinned: !note.isPinned }))
    dispatch(getNotes(filterParams))
  }

  const handleNoteDelete = async (note: Note) => {
    try {
      await new Promise((resolve, reject) => {
        setConfirmModal(<Modal resolve={resolve} reject={reject} />)
      })
      setConfirmModal(false)
      await dispatch(deleteNote({ noteCuid: note.cuid }))
      dispatch(getNotes(filterParams))
    } catch (e) {
      setConfirmModal(false)
    }
  }

  return (
    <Layout title="Notes">
      {confirmModal}
      <Panel header="Filters" className="mb-6">
        <div className="grid grid-cols-3 gap-2">
          <Input
            type="text"
            label="Search"
            placeholder="Search note body"
            value={search}
            onChange={(event) => {
              setSearch(event.target.value)
              setFilterParams({ ...filterParams, body: event.target.value })
            }}
          />
          <ComboboxMultiselect
            label="Tags"
            placeholder="Select tag(s)"
            onChange={(tags: string[]) => {
              setFilterParams({ ...filterParams, tags })
            }}
          />
          <div>
            <label htmlFor="">Date range</label>
            <Datepicker
              value={datePickerValue}
              onChange={(value) => {
                setDatePickerValue(value as { startDate: Date; endDate: Date })
                setFilterParams({
                  ...filterParams,
                  startDate: value?.startDate ? new Date(value.startDate) : undefined,
                  endDate: value?.endDate ? new Date(value.endDate) : undefined,
                })
              }}
              primaryColor="indigo"
              separator="–"
              containerClassName={'text-base font-base text-inherit relative'}
              inputClassName={classnames(getInputStyles(), 'px-2  py-1.5 inline-block')}
              showShortcuts={true}
              maxDate={new Date()}
            />
          </div>
        </div>
      </Panel>
      <div className="relative min-h-[16rem]">
      {listLoading ? (
        <div
          className="absolute inset-0 z-20 flex items-center justify-center bg-white/70 dark:bg-slate-900/60"
          aria-busy="true"
          aria-live="polite"
        >
          <div className="flex items-center gap-2 text-sm font-medium text-slate-600 dark:text-slate-300">
            <svg className="h-5 w-5 animate-spin" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" aria-hidden="true">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              />
            </svg>
            Loading...
          </div>
        </div>
      ) : null}
      {notes.filter((note) => note.isPinned).length > 0 && (
        <>
          <div className="flex flex-row justify-center w-full border-t border-slate-300 dark:border-slate-600 mt-4 -mb-4 text-bg-red-500">
            <span className="border-slate-200 bg-slate-100 dark:bg-slate-900 text-slate-500 dark:text-slate-400 relative -top-2 text-xs px-2 rounded-full">
              Pinned
            </span>
          </div>
          {notes
            .filter((note) => note.isPinned)
            .map((note) => (
              <NoteItem
                key={note.cuid}
                canModify={user?.email === note.author.email}
                isLast={false}
                onUpdate={handleNoteUpdate}
                onTogglePin={handleNoteTogglePin}
                onDelete={handleNoteDelete}
                note={note}
                searchTerm={search}
              />
            ))}
          <hr className="my-4 border-slate-300 dark:border-slate-600" />
        </>
      )}
      {notes
        .filter((note) => !note.isPinned)
        .map((note) => (
          <NoteItem
            key={note.cuid}
            canModify={user?.email === note.author.email}
            isLast={false}
            onUpdate={handleNoteUpdate}
            onTogglePin={handleNoteTogglePin}
            onDelete={handleNoteDelete}
            note={note}
            searchTerm={search}
          />
        ))}
      <Paginator className="mt-4" data={{ total, resultsPerPage, page }} handlePaginatorClick={handlePaginatorClick} />
      </div>
    </Layout>
  )
}

export default Notes
